import Box from '@mui/material/Box';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import Dialog from '@mui/material/Dialog';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import IconButton from '@mui/material/IconButton';
import LayerGroup from '@planet/maps/layer/Group';
import MVT from 'ol/format/MVT.js';
import Map from '@planet/maps/Map';
import OSM from '@planet/maps/source/OSM';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TileLayer from '@planet/maps/layer/WebGLTile';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import VectorTileLayer from '@planet/maps/layer/VectorTile';
import VectorTileSource from '@planet/maps/source/VectorTile';
import View from '@planet/maps/View';
import {Fill, Stroke, Style} from 'ol/style';
import {GRANERO_BASE_URL} from '@account/config';
import {unionExtents} from '@account/utils/az-geometry';
import {useAccessibilityZoneCollections} from '@account/hooks/data/products/useAccessibilityZoneCollections';
import {useAuth} from '@apps-common/auth-react';
import {useFlags} from '@hooks/useFlags';
import {useReservedAoiLayer} from '@account/hooks/data/products/useReservedAoiLayer';

const mvtFormat = new MVT();

// White casing under the Planet-blue stroke keeps thin/continental-scale
// boundaries legible on any basemap (and clears WCAG non-text contrast).
const azStyle = [
  new Style({
    stroke: new Stroke({color: 'rgba(255, 255, 255, 0.9)', width: 4}),
  }),
  new Style({
    fill: new Fill({color: 'rgba(0, 147, 208, 0.15)'}),
    stroke: new Stroke({color: 'rgba(0, 147, 208, 0.9)', width: 2}),
  }),
];

// Reserved AOIs — warm accent, distinct from the AZ blue and legible on top of
// its translucent fill.
const reservedStyle = [
  new Style({
    stroke: new Stroke({color: 'rgba(255, 255, 255, 0.9)', width: 3}),
  }),
  new Style({
    fill: new Fill({color: 'rgba(230, 126, 34, 0.35)'}),
    stroke: new Stroke({color: 'rgba(211, 84, 0, 0.95)', width: 2}),
  }),
];

// Paint only the AOIs this workspace has reserved. Each MVT feature carries a
// `pl:ref` whose trailing segment is the bare feature hash; if it's in this
// collection's reserved set we draw it in the reserved style, otherwise we
// return no style so it isn't rendered. The whole collection still tiles
// server-side, but only reserved AOIs are painted — no `?_relation` tile
// filter and no per-feature geometry fetch required.
export const makeReservedStyle = reservedHashids => feature => {
  const ref = feature.get('pl:ref');
  const hashid = ref && ref.split('/').pop();
  return hashid && reservedHashids.has(hashid) ? reservedStyle : undefined;
};

// Layer identity for the toggle control + legend.
export const RESERVED_LAYERS = [
  {key: 'az', label: 'Accessibility zone', swatch: 'rgba(0, 147, 208, 0.9)'},
  {key: 'reserved', label: 'Reserved', swatch: 'rgba(211, 84, 0, 0.95)'},
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const tileLoader = (tile, url, token) => {
  tile.setLoader((extent, resolution, featureProjection) => {
    fetch(url, {
      headers: {Authorization: `Bearer ${token}`},
    })
      .then(resp => {
        if (!resp.ok) {
          throw new Error(`Boundary tile request failed (${resp.status})`);
        }
        return resp.arrayBuffer();
      })
      .then(buf => {
        tile.setFeatures(
          tile.getFormat().readFeatures(buf, {extent, featureProjection})
        );
      })
      .catch(() => {
        // Mark the tile as empty so a failed request doesn't leave the tile
        // stuck loading (which blocks `rendercomplete` and hangs the fit).
        tile.setFeatures([]);
      });
  });
};

// Renders the OpenLayers map + boundary layers, fits to the combined extent,
// and keeps the canvas sized to its container (expand / fullscreen / resize).
const CoverageCanvas = ({
  collections,
  reservedCollections = [],
  visibility = {az: true, reserved: true},
  token,
}) => {
  const mapRef = useRef();
  const containerRef = useRef();
  const extentRef = useRef(null);
  const rafRef = useRef(null);

  const fitToExtent = useCallback((animate = true) => {
    const extent = extentRef.current;
    const map = mapRef.current;
    const view = map?.getView?.();
    if (!extent || !map || !view) return;
    if (map.updateSize) map.updateSize();
    view.fit(extent, {
      duration: animate && !prefersReducedMotion() ? 800 : 0,
      padding: [32, 32, 32, 32],
      maxZoom: 12,
    });
  }, []);

  // Combine collection extents and fit once the map is ready. @planet/maps
  // initializes the OL map asynchronously and its declarative <View> asserts a
  // default center/zoom, so a single mount-time fit can be a no-op or get
  // clobbered. Poll for the map, animate the fit once, then correct the frame
  // *instantly* on the first `rendercomplete` (after the view + tiles settle) if
  // it didn't stick — instant, so we never play a second fly-in over the first.
  useEffect(() => {
    const bboxes = collections
      .filter(c => c.extent?.spatial?.bbox?.[0])
      .map(c => c.extent.spatial.bbox[0]);
    extentRef.current = bboxes.length ? unionExtents(bboxes) : null;
    if (!extentRef.current) return undefined;

    let cancelled = false;
    let listeningMap = null;
    const refit = () => fitToExtent(false);
    const whenReady = () => {
      if (cancelled) return;
      const map = mapRef.current;
      if (!map?.getView?.()) {
        rafRef.current = requestAnimationFrame(whenReady);
        return;
      }
      fitToExtent();
      listeningMap = map;
      map.once?.('rendercomplete', refit);
    };
    whenReady();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // Detach the one-shot listener in case it never fired before unmount.
      listeningMap?.un?.('rendercomplete', refit);
    };
  }, [collections, fitToExtent]);

  // Keep the OL canvas sized to its container and reframe on resize
  // (expand-to-wide, fullscreen, window/split-screen changes).
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const map = mapRef.current;
      if (!map?.updateSize) return;
      map.updateSize();
      fitToExtent();
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [fitToExtent]);

  // Memoize per-collection reserved styles so the style prop keeps a stable
  // reference across renders — otherwise the reconciler would call setStyle
  // every render (and could recreate the layer, dropping its tile cache).
  // Keyed object, not a Map — `Map` is shadowed by the @planet/maps import.
  const reservedStyles = useMemo(() => {
    const styles = {};
    for (const {collectionId, reservedHashids} of reservedCollections) {
      styles[collectionId] = makeReservedStyle(reservedHashids);
    }
    return styles;
  }, [reservedCollections]);

  return (
    <Box ref={containerRef} sx={{height: '100%', width: '100%'}}>
      <Map ref={mapRef} style={{width: '100%', height: '100%'}}>
        <View options={{center: [0, 0], zoom: 2}} />
        <TileLayer>
          <OSM />
        </TileLayer>
        {/* Layers stay mounted; the group's `visible` toggles them so hiding a
            layer doesn't unmount it and refetch tiles when toggled back. */}
        <LayerGroup visible={visibility.az}>
          {collections.map(collection => (
            <VectorTileLayer key={`az-${collection.id}`} style={azStyle}>
              <VectorTileSource
                url={`${GRANERO_BASE_URL}/features/v1/ogc/boundaries/collections/${collection.id}/tiles/{z}/{x}/{y}`}
                options={{
                  format: mvtFormat,
                  tileLoadFunction: (tile, url) => tileLoader(tile, url, token),
                }}
              />
            </VectorTileLayer>
          ))}
        </LayerGroup>
        {/* Reserved AOIs sit ABOVE the AZ boundary (the AZ is the containing
            boundary and stays the bottom vector layer). Each collection is its
            own MVT layer; a style function paints only the features whose
            `pl:ref` hash is in that collection's reserved set, so unreserved
            features aren't drawn. Plain collection tiles — the reserved/
            unreserved split is done client-side, no `?_relation` filter. */}
        <LayerGroup visible={visibility.reserved}>
          {reservedCollections.map(({collectionId, dataset}) => (
            <VectorTileLayer
              key={`res-${collectionId}`}
              style={reservedStyles[collectionId]}
            >
              <VectorTileSource
                url={`${GRANERO_BASE_URL}/features/v1/ogc/${dataset}/collections/${collectionId}/tiles/{z}/{x}/{y}`}
                options={{
                  format: mvtFormat,
                  tileLoadFunction: (tile, url) => tileLoader(tile, url, token),
                }}
              />
            </VectorTileLayer>
          ))}
        </LayerGroup>
      </Map>
    </Box>
  );
};

CoverageCanvas.propTypes = {
  collections: PropTypes.arrayOf(PropTypes.object).isRequired,
  reservedCollections: PropTypes.arrayOf(PropTypes.object),
  visibility: PropTypes.object,
  token: PropTypes.string,
};

// Compact map-corner control: independently show/hide each boundary layer.
const LayerToggles = ({visibility, onToggle, reservedCount, enumerated}) => (
  <Paper
    elevation={2}
    sx={{
      position: 'absolute',
      top: 8,
      right: 8,
      px: 1,
      py: 0.5,
      borderRadius: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.92)',
    }}
  >
    <Stack sx={{gap: 0.25}}>
      {RESERVED_LAYERS.map(({key, label, swatch}) => (
        <Stack
          key={key}
          direction="row"
          onClick={() => onToggle(key)}
          sx={{
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            opacity: visibility[key] ? 1 : 0.45,
            userSelect: 'none',
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.25,
              border: `2px solid ${swatch}`,
              backgroundColor: visibility[key] ? swatch : 'transparent',
              flex: '0 0 auto',
            }}
          />
          <Typography variant="caption">
            {label}
            {key === 'reserved' && reservedCount > 0
              ? ` (${reservedCount.toLocaleString()}${enumerated ? '' : '+'})`
              : ''}
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Paper>
);

LayerToggles.propTypes = {
  visibility: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  reservedCount: PropTypes.number,
  enumerated: PropTypes.bool,
};

const AccessibilityZoneMap = ({
  azRefs,
  productAccessId,
  enableReservedAois = false,
  isWide = false,
  onToggleWide,
}) => {
  const auth = useAuth();
  const {showAllocationUI} = useFlags();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const {collections, isLoading, isError} =
    useAccessibilityZoneCollections(azRefs);

  // Reserved AOIs are drawn as their own MVT layers, colored client-side by
  // matching each collection's reserved feature hashes against the tiles. Gated
  // on the allocation flag (same audience as workspace allocation).
  const showReservedLayers = enableReservedAois && showAllocationUI;
  const {
    collections: reservedCollections,
    reservedDrawn,
    enumerated,
    isError: reservedIsError,
  } = useReservedAoiLayer({
    productAccessId,
    enabled: showReservedLayers,
  });

  const [visibility, setVisibility] = useState({
    az: true,
    reserved: true,
  });
  const toggleLayer = key => setVisibility(v => ({...v, [key]: !v[key]}));

  if (!azRefs?.length) return null;

  if (isLoading) {
    return (
      <Stack sx={{gap: 1}}>
        <Typography variant="subtitle1" sx={{fontWeight: 700}}>
          Coverage Area
        </Typography>
        <Skeleton variant="rectangular" height={280} sx={{borderRadius: 1}} />
      </Stack>
    );
  }

  if (isError || !collections.length) return null;

  const token = auth.user?.accessToken;
  const compactHeight = isWide ? 460 : 280;
  const hasReservedLayers =
    showReservedLayers && reservedCollections.length > 0;
  // The reserved query failing leaves the toggle and caption suppressed, which
  // looks identical to "no reservations." Surface it for the admins who can see
  // this layer at all.
  const reservedUnavailable = showReservedLayers && reservedIsError;
  const canvasProps = {
    collections,
    reservedCollections: hasReservedLayers ? reservedCollections : [],
    visibility,
    token,
  };

  return (
    <>
      <Stack sx={{gap: 1, height: '100%'}}>
        <Stack
          direction="row"
          sx={{alignItems: 'center', justifyContent: 'space-between'}}
        >
          <Typography variant="subtitle1" sx={{fontWeight: 700}}>
            Coverage Area
          </Typography>
          <Stack direction="row" sx={{gap: 1}}>
            {onToggleWide && (
              <Tooltip
                title={isWide ? 'Collapse map' : 'Expand map to full width'}
              >
                <IconButton
                  size="small"
                  onClick={onToggleWide}
                  aria-label={
                    isWide ? 'Collapse map' : 'Expand map to full width'
                  }
                >
                  {isWide ? (
                    <CloseFullscreenIcon fontSize="small" />
                  ) : (
                    <OpenInFullIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View map fullscreen">
              <IconButton
                size="small"
                onClick={() => setIsFullscreen(true)}
                aria-label="View map fullscreen"
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {reservedUnavailable && (
          <Typography variant="caption" color="text.secondary">
            Reserved areas unavailable
          </Typography>
        )}

        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
            borderRadius: 1,
            flex: 1,
            minHeight: compactHeight,
            position: 'relative',
          }}
        >
          <CoverageCanvas {...canvasProps} />
          {hasReservedLayers && (
            <LayerToggles
              visibility={visibility}
              onToggle={toggleLayer}
              reservedCount={reservedDrawn}
              enumerated={enumerated}
            />
          )}
          {hasReservedLayers && reservedDrawn > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                pointerEvents: 'none',
              }}
            >
              {enumerated
                ? `${reservedDrawn.toLocaleString()} reserved ${
                    reservedDrawn === 1 ? 'area' : 'areas'
                  }`
                : `showing ${reservedDrawn.toLocaleString()}+ reserved areas`}
            </Typography>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{sx: {height: '90vh'}}}
      >
        <Stack sx={{height: '100%'}}>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="h6">Coverage Area</Typography>
            <IconButton
              onClick={() => setIsFullscreen(false)}
              aria-label="Close fullscreen map"
            >
              <CloseFullscreenIcon />
            </IconButton>
          </Stack>
          <Box sx={{flex: 1, minHeight: 0, position: 'relative'}}>
            {isFullscreen && <CoverageCanvas {...canvasProps} />}
            {isFullscreen && hasReservedLayers && (
              <LayerToggles
                visibility={visibility}
                onToggle={toggleLayer}
                reservedCount={reservedDrawn}
                enumerated={enumerated}
              />
            )}
          </Box>
        </Stack>
      </Dialog>
    </>
  );
};

AccessibilityZoneMap.propTypes = {
  azRefs: PropTypes.arrayOf(PropTypes.string),
  productAccessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  enableReservedAois: PropTypes.bool,
  isWide: PropTypes.bool,
  onToggleWide: PropTypes.func,
};

export default AccessibilityZoneMap;
